/**
 * 一覧の共通クエリ（02 2.3.1）と応答の形（02 2.4・2.9）。
 */
import { z } from 'zod';
import { zCode } from './common.schema';

export const DEFAULT_PER_PAGE = 20;
export const MAX_PER_PAGE = 100;
/** 1 回の一覧で Query により読み出す件数の上限（D-010。超えたら 422 LIST_001） */
export const LIST_READ_LIMIT = 10000;

export const DeletedFilterSchema = z.enum(['exclude', 'only'], { message: 'MSG-V-020' });

/**
 * 一覧のクエリを組み立てる。sort は許可リストの値か、先頭に `-` を付けた降順（D-009）。
 */
export function buildListQuerySchema<const T extends readonly [string, ...string[]]>(
  sort_values: T,
  default_sort: string,
) {
  const sort_candidates = sort_values.flatMap((value) => [value, `-${value}`]) as [string, ...string[]];
  return z.object({
    page: z.coerce.number().int().min(1).default(1),
    per_page: z.coerce.number().int().min(1).max(MAX_PER_PAGE).default(DEFAULT_PER_PAGE),
    sort: z.enum(sort_candidates, { message: 'MSG-V-020' }).default(default_sort),
    q: z.string().trim().min(1).max(100).optional(),
    deleted: DeletedFilterSchema.default('exclude'),
  });
}

export const ListMetaSchema = z.object({
  page: z.number().int(),
  per_page: z.number().int(),
  total_count: z.number().int(),
  total_pages: z.number().int(),
});

export type ListMeta = z.infer<typeof ListMetaSchema>;

/**
 * 本文を受け取らない更新系 API の本文（243 IT-104・IT-430：02 2.10 D-019「定義外のプロパティがあれば 400」）。
 * 本文なし（undefined）・{} は通し、項目が 1 つでもあれば 400 VALIDATION_001（その項目に MSG-V-020）
 */
export const EmptyBodySchema = z.preprocess((value) => value ?? {}, z.object({}).strict());

/** 一括削除の本文（1〜100 件・重複不可：02 2.9） */
export const BulkDeleteRequestSchema = z
  .object({
    codes: z
      .array(zCode)
      .min(1, 'MSG-V-020')
      .max(100, 'MSG-V-020')
      .refine((codes) => new Set(codes).size === codes.length, 'MSG-V-020'),
  })
  .strict();

/** 共通属性（02 5.2・要件書 5.4） */
export const CommonAttributesSchema = z.object({
  created_at: z.string(),
  created_by: z.string(),
  updated_at: z.string(),
  updated_by: z.string(),
  version: z.number().int(),
});

export const ErrorDetailSchema = z.object({ field: z.string(), code: z.string(), message: z.string() });

export const ErrorResponseSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.union([z.array(ErrorDetailSchema), z.record(z.unknown())]),
    trace_id: z.string(),
  }),
});

/** 1 件の応答 `{ data }` */
export function dataResponseSchema<T extends z.ZodTypeAny>(data_schema: T) {
  return z.object({ data: data_schema });
}

/** 一覧の応答 `{ data, meta }` */
export function listResponseSchema<T extends z.ZodTypeAny>(item_schema: T) {
  return z.object({ data: z.array(item_schema), meta: ListMetaSchema });
}
